import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import { Handshake } from "lucide-react";

/**
 * Match Context — two-step mutual match.
 *
 * - request(targetId)            → sends a 🤝 match request (becomes "pending outgoing")
 * - accept(requestId)            → accept an incoming pending request → confirmed mutual
 * - decline(requestId)           → decline an incoming pending request
 * - cancelOrUnmatch(targetId)    → cancel my pending outgoing OR un-match an accepted match
 * - dismissAuto(targetId)        → dismiss an auto-suggested match
 * - refresh()                    → reload everything
 *
 * Status getter:
 *   statusFor(targetId) → "none" | "outgoing" | "incoming" | "confirmed"
 */
const MatchContext = createContext({
  data: { confirmed: [], pending_outgoing: [], pending_incoming: [], auto_matches: [] },
  incomingCount: 0,
  statusFor: () => "none",
  request: async () => {},
  accept: async () => {},
  decline: async () => {},
  cancelOrUnmatch: async () => {},
  dismissAuto: async () => {},
  refresh: async () => {},
});

const EMPTY = {
  confirmed: [],
  pending_outgoing: [],
  pending_incoming: [],
  auto_matches: [],
  requested_ids: [],
  confirmed_ids: [],
  incoming_ids: [],
  incoming_count: 0,
};

export const MatchProvider = ({ children }) => {
  const { user } = useAuth();
  const [data, setData] = useState(EMPTY);

  const refresh = useCallback(async () => {
    if (!user) { setData(EMPTY); return; }
    try {
      const res = await axios.get(`${API}/matches`);
      setData({ ...EMPTY, ...res.data });
    } catch { /* ignore */ }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const statusFor = useCallback((targetId) => {
    if (!targetId || !user) return "none";
    if (data.confirmed_ids?.includes(targetId)) return "confirmed";
    if (data.requested_ids?.includes(targetId)) return "outgoing";
    if (data.incoming_ids?.includes(targetId)) return "incoming";
    return "none";
  }, [data, user]);

  const request = async (targetId, targetName) => {
    if (!user || targetId === user.user_id) return;
    try {
      const res = await axios.post(`${API}/matches/request`, { target_user_id: targetId });
      if (res.data.status === "accepted") {
        toast.success(`You matched with ${targetName || "this member"}! 🤝`);
      } else {
        toast.success(`Match request sent${targetName ? ` to ${targetName}` : ""}.`);
      }
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send match request");
    }
  };

  const accept = async (requestId) => {
    try {
      await axios.post(`${API}/matches/accept/${requestId}`);
      toast.success("Match confirmed! 🤝");
      await refresh();
    } catch { toast.error("Could not accept"); }
  };

  const decline = async (requestId) => {
    try {
      await axios.post(`${API}/matches/decline/${requestId}`);
      toast.success("Request declined");
      await refresh();
    } catch { toast.error("Could not decline"); }
  };

  const cancelOrUnmatch = async (targetId) => {
    try {
      await axios.delete(`${API}/matches/request/${targetId}`);
      toast.success("Removed");
      await refresh();
    } catch { toast.error("Could not remove"); }
  };

  const dismissAuto = async (targetId) => {
    try {
      await axios.post(`${API}/matches/dismiss/${targetId}`);
      await refresh();
    } catch { /* ignore */ }
  };

  return (
    <MatchContext.Provider
      value={{
        data,
        incomingCount: data.incoming_count || 0,
        statusFor,
        request,
        accept,
        decline,
        cancelOrUnmatch,
        dismissAuto,
        refresh,
      }}
    >
      {children}
    </MatchContext.Provider>
  );
};

export const useMatches = () => useContext(MatchContext);

/**
 * Compact 🤝 button for member cards / profile pages.
 * Behavior:
 *  - none      → send request   (black w/ white handshake)
 *  - outgoing  → cancel request (pink, "Requested")
 *  - incoming  → quick accept   (blue, "Accept")
 *  - confirmed → un-match       (filled blue, "Matched")
 */
export const MatchButton = ({ targetUserId, targetName, className = "", showLabel = false }) => {
  const { user } = useAuth();
  const { statusFor, request, cancelOrUnmatch, accept, data } = useMatches();
  if (!user || user.user_id === targetUserId) return null;

  const status = statusFor(targetUserId);

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (status === "none") return request(targetUserId, targetName);
    if (status === "outgoing") return cancelOrUnmatch(targetUserId);
    if (status === "confirmed") return cancelOrUnmatch(targetUserId);
    if (status === "incoming") {
      const r = data.pending_incoming.find((x) => x.user_id === targetUserId);
      if (r?.request_id) return accept(r.request_id);
    }
  };

  const base = "inline-flex items-center gap-1.5 justify-center transition-colors rounded-full border-2 font-mono uppercase tracking-wider text-[10px]";
  const stylesByStatus = {
    none:      "bg-black text-white border-black hover:bg-[#5170FF] hover:border-[#5170FF]",
    outgoing:  "bg-[#FFC2E8] text-black border-black hover:bg-[#FF2D87] hover:text-white",
    incoming:  "bg-[#5170FF] text-white border-[#5170FF] hover:bg-[#3a55ee]",
    confirmed: "bg-[#5170FF] text-white border-[#5170FF] hover:bg-black",
  };
  const labelByStatus = {
    none: "Match",
    outgoing: "Requested",
    incoming: "Accept",
    confirmed: "Matched",
  };
  const titleByStatus = {
    none: "Send a match request",
    outgoing: "Pending — click to cancel",
    incoming: "They want to match — click to accept",
    confirmed: "Matched — click to un-match",
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      title={titleByStatus[status]}
      data-testid={`match-${status}-${targetUserId}`}
      className={`${base} ${stylesByStatus[status]} ${showLabel ? "px-2.5 py-1" : "w-8 h-8"} ${className}`}
    >
      <Handshake className={showLabel ? "w-3 h-3" : "w-4 h-4"} />
      {showLabel && <span>{labelByStatus[status]}</span>}
    </button>
  );
};
